import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ChipOption<T extends string = string> = {
  value: T;
  label: string;
};

type ChipMultiSelectProps<T extends string> = {
  options: ChipOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
  /** Shown when nothing is selected. */
  emptyHint?: string;
  className?: string;
  'aria-label'?: string;
};

/**
 * Closed-vocabulary multi-select as toggle chips.
 * Prefer this over freeform comma fields when typos would break filters.
 */
export function ChipMultiSelect<T extends string>({
  options,
  value,
  onChange,
  disabled,
  emptyHint = 'Select one or more',
  className,
  'aria-label': ariaLabel,
}: ChipMultiSelectProps<T>) {
  const selected = new Set(value);

  const toggle = (opt: T) => {
    if (disabled) return;
    if (selected.has(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  };

  return (
    <div className={cn('space-y-1.5', className)} role="group" aria-label={ariaLabel}>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.has(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              onClick={() => toggle(opt.value)}
              className={cn(
                'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                on
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
              )}
            >
              {on && <Check className="h-3 w-3 shrink-0" aria-hidden />}
              {opt.label}
            </button>
          );
        })}
      </div>
      {value.length === 0 && (
        <p className="text-[11px] text-gray-400">{emptyHint}</p>
      )}
    </div>
  );
}
